import { useEffect, useState } from 'react';
import AuthService from '../services/auth.service';
import { useAppAuth } from './useAppAuth';

/**
 * True once an ApiService request will actually carry an Authorization header.
 *
 * WHY THIS EXISTS
 * ---------------
 * There are two answers to "are we authenticated?", and on a cold page load they
 * disagree for a moment:
 *
 *   - `useAppAuth().user` — the React context, populated when the auth provider
 *     hydrates. Reactive, so gating on it re-runs effects when auth lands.
 *   - `AuthService.getAccessToken()` — the singleton chain ApiService actually
 *     reads to build the header.
 *
 * AuthenticatedRootPage copies the token from the first into the second, from an
 * effect. React runs child effects BEFORE parent effects, so a child that fires
 * a request as soon as the context has a user sends it before the singleton has
 * the token — no header, 401. It resolves a beat later, which is why the bug
 * only shows up on direct navigation and never when moving around inside the SPA
 * (by then the singleton is long since populated).
 *
 * Gating on the context alone is therefore not enough: it answers a different
 * question than the one that decides whether the request is authenticated.
 *
 * WHY IT POLLS
 * ------------
 * Nothing notifies when the singleton is populated — `setAuthState` deliberately
 * doesn't fire listeners, because doing so caused render loops. Rather than
 * change that shared behaviour, this watches for the token to appear.
 *
 * It only polls when the context already has a token, i.e. we know auth is real
 * and are only waiting for the hand-off. An anonymous visitor (the public docs
 * page) returns false immediately and never polls, so no request goes out and
 * there is no 401 noise.
 */
const POLL_MS = 50;
const GIVE_UP_MS = 5000;

export function useApiAuthReady(): boolean {
  const { user } = useAppAuth();
  const contextToken = user?.accessToken || null;

  const [ready, setReady] = useState(
    () => !!AuthService.getInstance().getAccessToken(),
  );

  useEffect(() => {
    if (ready || !contextToken) return;

    const authService = AuthService.getInstance();
    // Check immediately — the hand-off may already have happened between render
    // and this effect running.
    if (authService.getAccessToken()) {
      setReady(true);
      return;
    }

    const started = Date.now();
    const id = setInterval(() => {
      if (authService.getAccessToken()) {
        setReady(true);
        clearInterval(id);
      } else if (Date.now() - started > GIVE_UP_MS) {
        // Bounded: if the token never arrives, stop rather than poll forever.
        // Callers show their own error; a hung spinner would be worse.
        clearInterval(id);
      }
    }, POLL_MS);

    return () => clearInterval(id);
  }, [ready, contextToken]);

  return ready;
}
